import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

interface UsageEntry {
  id: string
  model: string
  provider: string
  prompt_preview: string
  response_preview: string
  input_tokens: number
  output_tokens: number
  total_tokens: number
  input_cost_usd: number
  output_cost_usd: number
  total_cost_usd: number
  latency_ms: number
  cache_hit: boolean
  created_at: string
}

export default function AIUsagePage() {
  const [entries, setEntries] = useState<UsageEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadUsage = async () => {
      try {
        const { data, error } = await supabase
          .from('ai_usage_log')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100)
        if (!error && data) setEntries(data)
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    loadUsage()
  }, [])

  const totalCost = entries.reduce((sum, e) => sum + (e.total_cost_usd || 0), 0)
  const totalTokens = entries.reduce((sum, e) => sum + (e.total_tokens || 0), 0)
  const cacheHits = entries.filter(e => e.cache_hit).length

  if (loading) return <div className="p-6 text-gray-400">Loading usage data...</div>

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold text-[var(--color-text-primary)] mb-4">📊 AI Usage & Costs</h1>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">Total Cost</div>
          <div className="text-lg font-bold text-green-400">${totalCost.toFixed(4)}</div>
        </div>
        <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">Total Calls</div>
          <div className="text-lg font-bold text-[var(--color-text-primary)]">{entries.length}</div>
        </div>
        <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">Total Tokens</div>
          <div className="text-lg font-bold text-[var(--color-text-primary)]">{totalTokens.toLocaleString()}</div>
        </div>
        <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">Cache Hits</div>
          <div className="text-lg font-bold text-blue-400">{cacheHits}/{entries.length}</div>
        </div>
      </div>

      {/* Table */}
      {entries.length === 0 ? (
        <div className="text-center text-[var(--color-text-muted)] py-12">
          No AI calls logged yet. Send a message from the Chat page to start tracking.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-[var(--color-text-muted)]">
                <th className="text-left py-2 px-2">Time</th>
                <th className="text-left py-2 px-2">Model</th>
                <th className="text-left py-2 px-2">Prompt</th>
                <th className="text-right py-2 px-2">Input</th>
                <th className="text-right py-2 px-2">Output</th>
                <th className="text-right py-2 px-2">Cost</th>
                <th className="text-right py-2 px-2">Latency</th>
                <th className="text-center py-2 px-2">Cache</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]">
                  <td className="py-2 px-2 whitespace-nowrap">{new Date(entry.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="py-2 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      entry.provider === 'deepseek' ? 'bg-emerald-100 text-emerald-800' :
                      entry.provider === 'gemini' ? 'bg-blue-100 text-blue-800' :
                      entry.provider === 'claude' ? 'bg-purple-100 text-purple-800' :
                      entry.provider === 'openai' ? 'bg-teal-100 text-teal-800' :
                      'bg-gray-200 text-gray-800'
                    }`}>
                      {entry.model}
                    </span>
                  </td>
                  <td className="py-2 px-2 max-w-[200px] truncate text-[var(--color-text-muted)]" title={entry.prompt_preview}>{entry.prompt_preview || '—'}</td>
                  <td className="py-2 px-2 text-right">{entry.input_tokens?.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right">{entry.output_tokens?.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right font-medium text-green-400">${entry.total_cost_usd?.toFixed(4)}</td>
                  <td className="py-2 px-2 text-right">{(entry.latency_ms / 1000).toFixed(1)}s</td>
                  <td className="py-2 px-2 text-center">{entry.cache_hit ? '✅' : '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--color-border)] font-bold">
                <td className="py-2 px-2" colSpan={3}>Total</td>
                <td className="py-2 px-2 text-right">{entries.reduce((s, e) => s + (e.input_tokens || 0), 0).toLocaleString()}</td>
                <td className="py-2 px-2 text-right">{entries.reduce((s, e) => s + (e.output_tokens || 0), 0).toLocaleString()}</td>
                <td className="py-2 px-2 text-right text-green-400">${totalCost.toFixed(4)}</td>
                <td className="py-2 px-2 text-right">{(entries.reduce((s, e) => s + (e.latency_ms || 0), 0) / 1000).toFixed(0)}s</td>
                <td className="py-2 px-2 text-center">{cacheHits}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
